import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid2,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    Box,
    Typography,
} from '@mui/material';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { getVenue } from '@openapi/venues/venues';
import useNotify from '../hooks/useNotify';
import { CUSTOM_FIELDS_TYPES } from '../helpers/constants';
import UploadImageInput from '../components/atom/UploadImageInput';

function ImageTranslationsModal({
    showModal,
    MODAL_TYPE,
    translations,
    setTranslations,
    item,
    column,
    intermediateColumn = null, // optional
    venueId,
    originalString,
    type, // optional (can be single, multi default is single)
}) {
    const { t } = useTranslation();
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [localTranslations, setLocalTranslations] = useState(translations || {});
    const { notifyError } = useNotify();

    // default to "columns.<column>" if intermediateColumn is not provided
    const itemLabel = intermediateColumn
        ? t(`${item}.${intermediateColumn}.${column}`)
        : t(`${item}.columns.${column}`);
    // if the type is multi, we need to split the original string by new line,
    // otherwise we can use the original string directly
    const originalStringValue = Array.isArray(originalString)
        ? originalString.join('\n')
        : String(originalString).replace(/\\n/g, '\n');

    const getAvailableLanguages = useCallback(async () => {
        try {
            const result = await getVenue(Number(venueId));
            setAvailableLanguages(result?.availableLanguages || []);
        } catch (error) {
            notifyError(error);
        }
    }, [venueId, notifyError]);

    useEffect(() => {
        getAvailableLanguages();
    }, [venueId, getAvailableLanguages]);

    return (
        <Dialog
            aria-labelledby="translations-modal"
            open
            maxWidth="md"
            fullWidth
            onClose={() => showModal(MODAL_TYPE.NONE)}
        >
            <DialogTitle>
                {itemLabel} ({t('common.translations')})
            </DialogTitle>
            <DialogContent>
                <Grid2 container>
                    <DialogContentText id="alert-dialog-info">
                        <Alert sx={{ mt: 0.5, mb: 2 }} severity="info">
                            {t('common.languageInfo')}
                        </Alert>
                    </DialogContentText>
                    <Grid2 size={12}>
                        {originalStringValue && (
                            <>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {`${itemLabel} (${t('common.original')}): ${originalStringValue}`}
                                </Typography>
                                <Box sx={{ p: 1 }} style={{ maxWidth: '400px' }}>
                                    <img
                                        src={originalStringValue}
                                        alt=""
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '400px',
                                            display: 'block',
                                        }}
                                    />
                                </Box>
                            </>
                        )}
                    </Grid2>
                    {availableLanguages.map((language) => (
                        <Grid2
                            key={language}
                            container
                            spacing={2}
                            size={{ xs: 12 }}
                            alignItems="center"
                        >
                            {type === CUSTOM_FIELDS_TYPES.DROPDOWN ? (
                                <>
                                    <Grid2 size={{ xs: 4 }}>
                                        <Autocomplete
                                            options={language}
                                            value={language}
                                            onChange={(_, newValue) => {
                                                setLocalTranslations({
                                                    ...localTranslations,
                                                    [language]: newValue,
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('common.language')}
                                                    fullWidth
                                                />
                                            )}
                                            getOptionLabel={(option) => {
                                                return `${option}`;
                                            }}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 8 }}>
                                        <TableContainer component={Paper} sx={{ mt: 2 }}>
                                            <Table>
                                                <TableBody>
                                                    {(originalString || []).map(
                                                        (original, index) => (
                                                            <TableRow key={index}>
                                                                <TableCell>
                                                                    <Box sx={{ p: 1 }}>
                                                                        <img
                                                                            src={original}
                                                                            alt=""
                                                                            style={{
                                                                                maxWidth: '100px',
                                                                                maxHeight: '100px',
                                                                                display: 'block',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ p: 1 }}>
                                                                        <img
                                                                            src={
                                                                                (localTranslations[
                                                                                    language
                                                                                ] || [])[index] ||
                                                                                ''
                                                                            }
                                                                            alt=""
                                                                            style={{
                                                                                maxWidth: '100px',
                                                                                maxHeight: '100px',
                                                                                display: 'block',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <TextField
                                                                        fullWidth
                                                                        value={
                                                                            (localTranslations[
                                                                                language
                                                                            ] || [])[index] || ''
                                                                        }
                                                                        onChange={(ev) => {
                                                                            const updatedTranslations =
                                                                                [
                                                                                    ...(localTranslations[
                                                                                        language
                                                                                    ] || []),
                                                                                ];
                                                                            updatedTranslations[
                                                                                index
                                                                            ] = ev.target.value;
                                                                            setLocalTranslations({
                                                                                ...localTranslations,
                                                                                [language]:
                                                                                    updatedTranslations,
                                                                            });
                                                                        }}
                                                                        label={t('image.imageUrl')}
                                                                        sx={{ mt: 1 }}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ),
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid2>
                                </>
                            ) : (
                                <>
                                    <Grid2 size={{ xs: 4 }}>
                                        <Autocomplete
                                            options={language}
                                            value={language}
                                            onChange={(_, newValue) => {
                                                setLocalTranslations({
                                                    ...localTranslations,
                                                    [language]: newValue,
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={t('common.language')}
                                                    fullWidth
                                                />
                                            )}
                                            getOptionLabel={(option) => {
                                                return `${option}`;
                                            }}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 8 }}>
                                        <UploadImageInput
                                            multiple={type === CUSTOM_FIELDS_TYPES.MULTI}
                                            value={localTranslations[language] || ''}
                                            setFieldValue={(url) =>
                                                setLocalTranslations({
                                                    ...localTranslations,
                                                    [language]: url,
                                                })
                                            }
                                            title={`${itemLabel} (${language})`}
                                        />
                                    </Grid2>
                                </>
                            )}
                        </Grid2>
                    ))}
                </Grid2>
            </DialogContent>
            <DialogActions>
                <CommonButton
                    variant="inherit"
                    onClick={() => showModal(MODAL_TYPE.NONE)}
                    label={t('common.cancel')}
                    buttonType="secondary"
                />
                <CommonButton
                    onClick={() => {
                        setTranslations(localTranslations);
                        showModal(MODAL_TYPE.NONE);
                    }}
                    label={t('common.set')}
                    buttonType="primary"
                />
            </DialogActions>
        </Dialog>
    );
}

export default ImageTranslationsModal;
